import { minBy } from 'es-toolkit';
import React, { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { addComponentSplices } from '../../store/actions/insertActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getInsertingObjectInfo } from '../../store/selectors/insertionObjectSelectors';
import { getSpliceInsertionSnaps } from '../../store/selectors/spliceSelectors';
import { useMousePositionInScene } from '../hooks/useMousePositionInScene';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { BasicSpliceVisual, SpliceDimensionsVisual } from './SpliceVisual';

export function SpliceInsertionVisual() {
  const isInsertingSplice = useAppSelector(getInsertingObjectInfo)?.type === 'splice';

  return isInsertingSplice ? <SpliceInsertionVisualInternals /> : null;
}

function SpliceInsertionVisualInternals() {
  const dispatch = useAppDispatch();
  const componentDesign = useAppSelector(getActiveComponentDesign);
  const snaps = useAppSelector(getSpliceInsertionSnaps);
  const mousePosition = useMousePositionInScene();

  const closestSnap = useMemo(
    () =>
      snaps && mousePosition
        ? (minBy(
            snaps.map((snap) => ({ snap, distance: snap.location.distanceTo(mousePosition) })),
            ({ distance }) => distance,
          )?.snap ?? null)
        : null,
    [mousePosition, snaps],
  );

  useEffect(() => {
    if (!componentDesign) return;

    const insertSnap = () => {
      if (!closestSnap) return;
      dispatch(addComponentSplices(componentDesign, closestSnap.member, closestSnap.distance));
    };

    threeJsDom.addGlobalListener('click', insertSnap);
    return () => threeJsDom.removeGlobalListener('click', insertSnap);
  }, [closestSnap, componentDesign, dispatch]);

  if (!closestSnap || !componentDesign) return null;

  const { segment, thickness, member } = closestSnap;

  return (
    <>
      <BasicSpliceVisual highlighted spliceSegment={segment} spliceThickness={thickness} />
      <SpliceDimensionsVisual
        component={componentDesign.component}
        spliceSegment={segment}
        componentDesignGuid={componentDesign.guid}
        member1={member}
      />
    </>
  );
}
